import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { activeUsersResponseSchema } from "@src/appV2/ExperimentalHomeHealth/lib/types";
import { useCurrentUserId } from "@src/appV2/ExperimentalHomeHealth/lib/useCurrentUserId";
import { isMongoObjectId } from "@src/appV2/Placements/types/assignCandidate.schema";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { getVisitQueryKey } from "../../Cases/api/useVisit";
import { bulkWorkerSchema, type VisitOccurrence } from "../../Cases/types";
import {
  type VisitOccurrencesParams,
  visitOccurrencesParamsSchema,
  visitOccurrencesResponseSchema,
  visitOccurrencesUsersResponseSchema,
} from "../types";

const VISIT_OCCURRENCES_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/visit-occurrences`;
const BULK_WORKER_URL = `${environmentConfig.REACT_APP_API_MAIN_URL}/api/agentprofile/bulk`;
const ACTIVE_USERS_URL = `${environmentConfig.REACT_APP_API_MAIN_URL}/api/facilityUser/activeUsers`;
const BULK_WORKER_BATCH_SIZE = 50;

export function getVisitOccurrencesQueryKey(params: {
  workplaceId: string;
  params: VisitOccurrencesParams;
}) {
  const keyWithFilter: Array<
    | string
    | VisitOccurrencesParams["filter"]
    | VisitOccurrencesParams["sort"]
    | VisitOccurrencesParams["page"]
  > = ["home-health", params.workplaceId, "visit-occurrences", params.params.filter];

  if (isDefined(params.params.sort)) {
    keyWithFilter.push(params.params.sort);
  }

  if (isDefined(params.params.page)) {
    keyWithFilter.push(params.params.page);
  }

  return keyWithFilter;
}

export function useVisitOccurrences(
  params: VisitOccurrencesParams,
  options: UseQueryOptions<VisitOccurrence[], unknown, VisitOccurrence[]> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: getVisitOccurrencesQueryKey({ workplaceId, params }),
    queryFn: async () => {
      const response = await get({
        url: VISIT_OCCURRENCES_URL.replace(":workplaceId", workplaceId),
        requestSchema: visitOccurrencesParamsSchema,
        queryParams: params,
        responseSchema: visitOccurrencesResponseSchema,
      });

      return response.data.data;
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    onSuccess: (visits) => {
      visits.forEach((visit) => {
        queryClient.setQueryData(getVisitQueryKey({ workplaceId, visitId: visit.id }), visit);
      });
    },
    ...options,
  });
}

export function useVisitOccurrencesUniqueWorkers() {
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: ["home-health", workplaceId, "visit-occurrences", "workers"],
    enabled: isMongoObjectId(workplaceId),
    queryFn: async () => {
      const workersResponse = await get({
        url: `${VISIT_OCCURRENCES_URL.replace(":workplaceId", workplaceId)}/workers`,
        responseSchema: visitOccurrencesUsersResponseSchema,
      });

      const userIds = workersResponse.data.data
        .map((userIdObject) => userIdObject.id)
        .filter(isMongoObjectId);

      const batches = Array.from(
        { length: Math.ceil(userIds.length / BULK_WORKER_BATCH_SIZE) },
        (_, index) => index
      ).map((index) =>
        userIds.slice(index * BULK_WORKER_BATCH_SIZE, (index + 1) * BULK_WORKER_BATCH_SIZE)
      );

      const bulkWorkerResponses = await Promise.all(
        batches.map(
          async (batch) =>
            await get({
              url: `${BULK_WORKER_URL}?workerIds=${batch.join(",")}`,
              responseSchema: bulkWorkerSchema,
            })
        )
      );
      const workerNames = bulkWorkerResponses.reduce((accumulator, current) => ({
        ...accumulator,
        ...current.data,
      }));

      return userIds.map((userId) => ({
        value: userId,
        label: workerNames.data[userId]?.name ?? `id: ${userId}`,
      }));
    },
    retry: 0,
  });
}

export function useVisitOccurrencesUniqueVerifiers() {
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: ["home-health", workplaceId, "visit-occurrences", "verifiers"],
    enabled: isMongoObjectId(workplaceId),
    queryFn: async () => {
      const activeUsersResponse = await get({
        url: ACTIVE_USERS_URL,
        queryParams: { facilityId: workplaceId },
        responseSchema: activeUsersResponseSchema,
      });

      return activeUsersResponse.data.users.map((user) => ({
        value: user._id,
        label: user.email,
      }));
    },
    retry: 0,
  });
}
